import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import {
	Button,
	ChildrenProps,
	Column,
	Conditional,
	Else,
	englishCommaJoinStrings,
	filterMap,
	getObjectValues,
	Heading,
	If,
	makeClassName,
	Modal,
	preventDefault,
	Row,
	toGrammaticalNumber,
	Tooltip,
	TruncatedText,
} from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	EditMode,
	FieldConditional,
	FieldElse,
	FieldIf,
	Form,
	FormState,
	FormTooltipButton,
	FormValues,
	SubFormField,
	SubFormFieldContentProps,
	SubFormFieldItemsProps,
	SubFormFieldProps,
	SubFormRenderProps,
	SubmitButton,
	useInternalField,
	useInternalFields,
} from "@hex-insights/forms";
import { Icon, IconContainer } from "../../../Components/Icon";
import {
	Announcement,
	CalendarEvent,
	CourseAssessment,
	CourseAssessmentSubmission,
	MediaAttachmentDetailQuery,
	Message,
	Post,
	StudentMedicalEvent,
	StudentNote,
	truncateDisplayItems,
	usePersonSelectQuery,
} from "../../../Utilities";
import {
	FileAttachmentPreview,
	LinkAttachmentPreview,
	VisualAttachmentPreview,
} from "../../Components/MediaAttachment";
import { MediaAttachmentDeleteButton } from "../../DeleteButtons";
import { PersonFormat } from "../../Format";
import { MediaAttachmentFormValues } from "../../FormUtilities";
import { PersonOrderFormValues } from "../../Order";
import { MediaAttachmentUtilities } from "../../Utilities";
import * as Field from "./Field";
import { useCreateForm, useDetailForm } from "./hooks";
import styles from "./custom-form.module.css";

export type SubFormProps<
	FV extends FormValues,
	SFV extends FormValues & Pick<MediaAttachmentFormValues.Create, "title" | "isFile" | "fileKey" | "fileContentType">,
> = SubFormFieldProps<FV, SFV>;

export function SubForm<
	FV extends FormValues,
	SFV extends FormValues & Pick<MediaAttachmentFormValues.Create, "title" | "isFile" | "fileKey" | "fileContentType">,
>({ formState, noHeader = true, formattedBlank = "", ...props }: SubFormProps<FV, SFV>) {
	return (
		<SubFormField
			formState={formState}
			formattedBlank={formattedBlank}
			noHeader={noHeader}
			Content={SubForm.Content}
			Items={SubForm.Items}
			{...props}
		/>
	);
}

SubForm.Content = function <
	FV extends FormValues,
	SFV extends FormValues & Pick<MediaAttachmentFormValues.Create, "title" | "isFile" | "fileKey" | "fileContentType">,
>({ addItem, blankItem, formState, children }: SubFormFieldContentProps<FV, SFV>) {
	const [uploadingCount, setUploadingCount] = React.useState(0);
	const uploadFiles = MediaAttachmentUtilities.useUploadFiles(
		formState,
		addItem,
		blankItem,
		uploadingCount,
		setUploadingCount,
	);

	const [isDragging, setIsDragging] = React.useState(false);

	const onDrop = React.useCallback<React.DragEventHandler>(
		(event) => {
			event.preventDefault();
			setIsDragging(false);
			uploadFiles(event.dataTransfer.files);
		},
		[uploadFiles],
	);

	return (
		<div
			onDragEnter={() => setIsDragging(true)}
			className={makeClassName(styles["sub-form__content"], isDragging ? styles["sub-form__content--dragging"] : "")}
		>
			<Column justify="spaced-start">{children}</Column>

			<If condition={isDragging || uploadingCount > 0}>
				<div
					onDragLeave={() => setIsDragging(false)}
					onDragOver={preventDefault}
					onDrop={onDrop}
					className={styles["sub-form__content__status"]}
				>
					<span className={styles["sub-form__content__status__content"]}>
						{uploadingCount > 0 ? "Uploading..." : "Drop files here to upload"}
					</span>
				</div>
			</If>
		</div>
	);
};

SubForm.Items = function <FV extends FormValues, SFV extends FormValues>({
	numItems,
	children,
}: SubFormFieldItemsProps<FV, SFV>) {
	const ref = React.useRef<HTMLDivElement | null>(null);
	const prevNumItemsRef = React.useRef(numItems);

	React.useEffect(() => {
		const prevNumItems = prevNumItemsRef.current;
		prevNumItemsRef.current = numItems;
		if (numItems <= prevNumItems) {
			return;
		}

		if (!(ref.current && ref.current.scrollTo)) {
			// scrollTo not defined in test environment (for storyshots)
			return;
		}
		ref.current.scrollTo({ left: ref.current.scrollWidth, top: 0, behavior: "smooth" });
	}, [numItems]);

	return (
		<div ref={ref} style={{ display: "flex", overflowX: "scroll" }}>
			{children}
		</div>
	);
};

export type SubFormSubFormProps<FV extends FormValues> = SubFormRenderProps<FV> & Partial<ChildrenProps>;

SubForm.SubForm = function <FV extends FormValues>({ formState, onRemoveClick, children }: SubFormSubFormProps<FV>) {
	return (
		<Form.Column
			style={{
				flexShrink: 0,
				width: "20rem",
				maxWidth: "100%",
				padding: "0.625rem",
				position: "relative",
			}}
		>
			<div style={{ position: "absolute", top: 0, right: 0, zIndex: 2 }}>
				<Button
					variant="secondary"
					size="small"
					onClick={onRemoveClick}
					disabled={formState.disabled}
					style={{ width: "1.25rem", height: "1.25rem", position: "relative" }}
				>
					<Icon.X
						size={14}
						style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
					/>
				</Button>
			</div>

			{children}
		</Form.Column>
	);
};

SubForm.AddButton = function <
	FV extends FormValues,
	SFV extends FormValues & Pick<MediaAttachmentFormValues.Create, "title" | "isFile" | "fileKey" | "fileContentType">,
>({ formState, addItem, disabled, blankItem }: AddSubFormButtonRenderProps<FV, SFV>) {
	const [uploadingCount, setUploadingCount] = React.useState(0);
	const uploadFiles = MediaAttachmentUtilities.useUploadFiles(
		formState,
		addItem,
		blankItem,
		uploadingCount,
		setUploadingCount,
	);

	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			uploadFiles(event.target.files);
		},
		[uploadFiles],
	);

	// dragging handled by `<SubForm.Content>`

	return (
		<label className={styles["sub-form__add-button"]}>
			<input type="file" value="" onChange={onChange} multiple disabled={disabled} style={{ display: "none" }} />

			<Column justify="spaced-center" align="center" className={styles["sub-form__add-button__content"]}>
				<IconContainer color="var(--theme-colors__blue--light)">
					<Icon.Paperclip size={32} color="var(--theme-colors__blue)" />
				</IconContainer>
				{uploadingCount > 0 ? "Uploading..." : "Attach File or Image"}
			</Column>
		</label>
	);
};

const fileFieldNames: ("fileKey" | "fileContentType")[] = ["fileKey", "fileContentType"];

const inMessageInternalFieldNames: (keyof MediaAttachmentFormValues.CreateInMessage)[] = [
	"displayOrder",
	"description",
	"announcementID",
	"calendarEventID",
	"courseAssessmentID",
	"courseAssessmentSubmissionID",
	"postID",
	"studentMedicalEventID",
	"studentNoteID",
	"taggedPersonIDs",
];

export type CreateFieldsInMessageProps = {
	formState: FormState<MediaAttachmentFormValues.CreateInMessage>;
	fileURL?: string;
};

export function CreateFieldsInMessage({ formState, fileURL }: CreateFieldsInMessageProps) {
	useInternalField(formState, "isFile");
	useInternalFields(formState, inMessageInternalFieldNames);

	return (
		<React.Fragment>
			<Field.Title formState={formState} />
			<FieldConditional>
				<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
					<Field.File formState={formState} url={fileURL} noHeader />
				</FieldIf>
				<FieldElse formState={formState} name="linkURL">
					<Field.LinkURL formState={formState} />
				</FieldElse>
			</FieldConditional>
		</React.Fragment>
	);
}

const inAnnouncementInternalFieldNames: (keyof MediaAttachmentFormValues.CreateInAnnouncement)[] = [
	"displayOrder",
	"description",
	"calendarEventID",
	"courseAssessmentID",
	"courseAssessmentSubmissionID",
	"messageID",
	"postID",
	"studentMedicalEventID",
	"studentNoteID",
	"taggedPersonIDs",
];

export type CreateFieldsInAnnouncementProps = {
	formState: FormState<MediaAttachmentFormValues.CreateInAnnouncement>;
	fileURL?: string;
};

export function CreateFieldsInAnnouncement({ formState, fileURL }: CreateFieldsInAnnouncementProps) {
	useInternalField(formState, "isFile");
	useInternalFields(formState, inAnnouncementInternalFieldNames);

	return (
		<React.Fragment>
			<FieldConditional>
				<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
					<Field.File formState={formState} url={fileURL} noHeader style={{ width: "100%" }} />
				</FieldIf>
				<FieldElse formState={formState} name="linkURL">
					<Field.LinkURL formState={formState} style={{ width: "100%" }} />
				</FieldElse>
			</FieldConditional>

			<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
				<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
			</FieldIf>
		</React.Fragment>
	);
}

const inCalendarEventInternalFieldNames: (keyof MediaAttachmentFormValues.CreateInCalendarEvent)[] = [
	"displayOrder",
	"description",
	"announcementID",
	"courseAssessmentID",
	"courseAssessmentSubmissionID",
	"messageID",
	"postID",
	"studentMedicalEventID",
	"studentNoteID",
	"taggedPersonIDs",
];

export type CreateFieldsInCalendarEventProps = {
	formState: FormState<MediaAttachmentFormValues.CreateInCalendarEvent>;
	fileURL?: string;
};

export function CreateFieldsInCalendarEvent({ formState, fileURL }: CreateFieldsInCalendarEventProps) {
	useInternalField(formState, "isFile");
	useInternalFields(formState, inCalendarEventInternalFieldNames);

	return (
		<React.Fragment>
			<FieldConditional>
				<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
					<Field.File formState={formState} url={fileURL} noHeader style={{ width: "100%" }} />
				</FieldIf>
				<FieldElse formState={formState} name="linkURL">
					<Field.LinkURL formState={formState} style={{ width: "100%" }} />
				</FieldElse>
			</FieldConditional>

			<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
				<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
			</FieldIf>
		</React.Fragment>
	);
}

const inCourseAssessmentInternalFieldNames: (keyof MediaAttachmentFormValues.CreateInCourseAssessment)[] = [
	"displayOrder",
	"description",
	"announcementID",
	"calendarEventID",
	"courseAssessmentSubmissionID",
	"messageID",
	"postID",
	"studentMedicalEventID",
	"studentNoteID",
	"taggedPersonIDs",
];

export type CreateFieldsInCourseAssessmentProps = {
	formState: FormState<MediaAttachmentFormValues.CreateInCourseAssessment>;
	fileURL?: string;
};

export function CreateFieldsInCourseAssessment({ formState, fileURL }: CreateFieldsInCourseAssessmentProps) {
	useInternalField(formState, "isFile");
	useInternalFields(formState, inCourseAssessmentInternalFieldNames);

	return (
		<React.Fragment>
			<FieldConditional>
				<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
					<Field.File formState={formState} url={fileURL} noHeader style={{ width: "100%" }} />
				</FieldIf>
				<FieldElse formState={formState} name="linkURL">
					<Field.LinkURL formState={formState} style={{ width: "100%" }} />
				</FieldElse>
			</FieldConditional>

			<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
				<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
			</FieldIf>
			<FieldIf formState={formState} name="description" condition={!!formState.formValues.fileKey}>
				<Field.Description
					formState={formState}
					noHeader
					placeholder="Add a description..."
					style={{ width: "100%" }}
				/>
			</FieldIf>
		</React.Fragment>
	);
}

const inCourseAssessmentSubmissionInternalFieldNames: (keyof MediaAttachmentFormValues.CreateInCourseAssessmentSubmission)[] =
	[
		"displayOrder",
		"description",
		"announcementID",
		"calendarEventID",
		"courseAssessmentID",
		"messageID",
		"postID",
		"studentMedicalEventID",
		"studentNoteID",
		"taggedPersonIDs",
	];

export type CreateFieldsInCourseAssessmentSubmissionProps = {
	formState: FormState<MediaAttachmentFormValues.CreateInCourseAssessmentSubmission>;
	fileURL?: string;
};

export function CreateFieldsInCourseAssessmentSubmission({
	formState,
	fileURL,
}: CreateFieldsInCourseAssessmentSubmissionProps) {
	useInternalField(formState, "isFile");
	useInternalFields(formState, inCourseAssessmentSubmissionInternalFieldNames);

	return (
		<React.Fragment>
			<FieldConditional>
				<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
					<Field.File formState={formState} url={fileURL} noHeader style={{ width: "100%" }} />
				</FieldIf>
				<FieldElse formState={formState} name="linkURL">
					<Field.LinkURL formState={formState} style={{ width: "100%" }} />
				</FieldElse>
			</FieldConditional>

			<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
				<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
			</FieldIf>
		</React.Fragment>
	);
}

const inPostVisualFileConditionalFields: (keyof Pick<
	MediaAttachmentFormValues.CreateInPost,
	"description" | "taggedPersonIDs"
>)[] = ["description", "taggedPersonIDs"];

const inPostInternalFieldNames: (keyof MediaAttachmentFormValues.CreateInPost)[] = [
	"displayOrder",
	"announcementID",
	"calendarEventID",
	"courseAssessmentID",
	"courseAssessmentSubmissionID",
	"messageID",
	"studentMedicalEventID",
	"studentNoteID",
];

export type CreateFieldsInPostProps = {
	formState: FormState<MediaAttachmentFormValues.CreateInPost>;
	fileURL?: string;
};

export function CreateFieldsInPost({ formState, fileURL }: CreateFieldsInPostProps) {
	useInternalField(formState, "isFile");
	useInternalFields(formState, inPostInternalFieldNames);

	return (
		<React.Fragment>
			<FieldConditional>
				<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
					<Field.File formState={formState} url={fileURL} noHeader style={{ width: "100%" }} />
				</FieldIf>
				<FieldElse formState={formState} name="linkURL">
					<Field.LinkURL formState={formState} style={{ width: "100%" }} />
				</FieldElse>
			</FieldConditional>

			<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
				<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
			</FieldIf>

			<FieldIf
				formState={formState}
				names={inPostVisualFileConditionalFields}
				condition={
					formState.formValues.isFile &&
					MediaAttachmentUtilities.isVisual({ fileContentType: formState.formValues.fileContentType })
				}
			>
				<Field.Description formState={formState} noHeader placeholder="Write a caption..." style={{ width: "100%" }} />
				<TaggedPeopleTooltipButton formState={formState} />
			</FieldIf>
		</React.Fragment>
	);
}

type TaggedPeopleTooltipButtonProps = {
	formState: FormState<Pick<MediaAttachmentFormValues.Base, "taggedPersonIDs">>;
};

function TaggedPeopleTooltipButton({ formState }: TaggedPeopleTooltipButtonProps) {
	const defaultPersonOrder = IndexForms.useOrderInput(PersonOrderFormValues.initial);
	const { data: personData } = usePersonSelectQuery({ variables: { order: defaultPersonOrder } });
	const selectedPeople = React.useMemo(
		() =>
			filterMap(
				personData?.personConnection.edges ?? [],
				(e) => formState.formValues.taggedPersonIDs.includes(e.node.id),
				(e) => e.node,
			),
		[personData, formState.formValues.taggedPersonIDs],
	);
	const { displayedItems, anyHidden, numHidden } = truncateDisplayItems(selectedPeople, 2);
	const displayedPersonNames = displayedItems.map((e) => PersonFormat.name(e));
	if (anyHidden) {
		displayedPersonNames.push(toGrammaticalNumber("Other", numHidden, true));
	}

	return (
		<FormTooltipButton formState={formState} className="tagged-people-form-tooltip-button">
			{({ formState }) => (
				<Tooltip isInteractable className="tooltip--big">
					<Tooltip.Header>
						<strong>Tag People</strong>
					</Tooltip.Header>
					<Tooltip.Body>
						<Form.Column>
							<Field.TaggedPeople.Select formState={formState} />
						</Form.Column>
					</Tooltip.Body>
				</Tooltip>
			)}
			{({ onClick, disabled }) => (
				<Button
					variant="tertiary"
					onClick={onClick}
					disabled={disabled}
					style={{ maxWidth: "100%", overflow: "hidden" }}
				>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<TruncatedText>
							<Conditional>
								<If condition={selectedPeople.length > 0}>Tagged: {englishCommaJoinStrings(displayedPersonNames)}</If>
								<Else>Click to Tag People</Else>
							</Conditional>
						</TruncatedText>

						<Icon.CaretDown size={14} />
					</Row>
				</Button>
			)}
		</FormTooltipButton>
	);
}

const inStudentMedicalEventInternalFieldNames: (keyof MediaAttachmentFormValues.CreateInStudentMedicalEvent)[] = [
	"displayOrder",
	"description",
	"announcementID",
	"calendarEventID",
	"courseAssessmentID",
	"courseAssessmentSubmissionID",
	"messageID",
	"postID",
	"studentNoteID",
	"taggedPersonIDs",
];

export type CreateFieldsInStudentMedicalEventProps = {
	formState: FormState<MediaAttachmentFormValues.CreateInStudentMedicalEvent>;
	fileURL?: string;
};

export function CreateFieldsInStudentMedicalEvent({ formState, fileURL }: CreateFieldsInStudentMedicalEventProps) {
	useInternalField(formState, "isFile");
	useInternalFields(formState, inStudentMedicalEventInternalFieldNames);

	return (
		<React.Fragment>
			<FieldConditional>
				<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
					<Field.File formState={formState} url={fileURL} noHeader style={{ width: "100%" }} />
				</FieldIf>
				<FieldElse formState={formState} name="linkURL">
					<Field.LinkURL formState={formState} style={{ width: "100%" }} />
				</FieldElse>
			</FieldConditional>

			<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
				<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
			</FieldIf>
		</React.Fragment>
	);
}

const inStudentNoteInternalFieldNames: (keyof MediaAttachmentFormValues.CreateInStudentNote)[] = [
	"displayOrder",
	"description",
	"announcementID",
	"calendarEventID",
	"courseAssessmentID",
	"courseAssessmentSubmissionID",
	"messageID",
	"postID",
	"studentMedicalEventID",
	"taggedPersonIDs",
];

export type CreateFieldsInStudentNoteProps = {
	formState: FormState<MediaAttachmentFormValues.CreateInStudentNote>;
	fileURL?: string;
};

export function CreateFieldsInStudentNote({ formState, fileURL }: CreateFieldsInStudentNoteProps) {
	useInternalField(formState, "isFile");
	useInternalFields(formState, inStudentNoteInternalFieldNames);

	return (
		<React.Fragment>
			<FieldConditional>
				<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
					<Field.File formState={formState} url={fileURL} noHeader style={{ width: "100%" }} />
				</FieldIf>
				<FieldElse formState={formState} name="linkURL">
					<Field.LinkURL formState={formState} style={{ width: "100%" }} />
				</FieldElse>
			</FieldConditional>

			<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
				<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
			</FieldIf>
		</React.Fragment>
	);
}

export type ControlledMultiEditFormProps = {
	mediaAttachments: Omit<
		MediaAttachmentDetailQuery["mediaAttachment"],
		| "announcement"
		| "calendarEvent"
		| "courseAssessment"
		| "courseAssessmentSubmission"
		| "message"
		| "post"
		| "studentMedicalEvent"
		| "studentNote"
	>[];
	announcementID?: Announcement["id"] | null;
	calendarEventID?: CalendarEvent["id"] | null;
	courseAssessmentID?: CourseAssessment["id"] | null;
	courseAssessmentSubmissionID?: CourseAssessmentSubmission["id"] | null;
	messageID?: Message["id"] | null;
	postID?: Post["id"] | null;
	studentMedicalEventID?: StudentMedicalEvent["id"] | null;
	studentNoteID?: StudentNote["id"] | null;
};

export function ControlledMultiEditForm({
	mediaAttachments,
	announcementID,
	calendarEventID,
	courseAssessmentID,
	courseAssessmentSubmissionID,
	messageID,
	postID,
	studentMedicalEventID,
	studentNoteID,
}: ControlledMultiEditFormProps) {
	const {
		isModalOpen: isCreateModalOpen,
		openModal: openCreateModal,
		closeModal: closeCreateModal,
	} = Modal.useToggle();

	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitMediaAttachments(mediaAttachments),
		[mediaAttachments],
	);

	return (
		<React.Fragment>
			<Column justify="spaced-start">
				<If condition={visualAttachments.length > 0}>
					<Row justify="spaced-start" overflow="wrap" withVerticalSpacing>
						{visualAttachments.map((mediaAttachment) => (
							<EditPreview
								key={mediaAttachment.id}
								mediaAttachment={mediaAttachment}
								announcementID={announcementID}
								calendarEventID={calendarEventID}
								courseAssessmentID={courseAssessmentID}
								courseAssessmentSubmissionID={courseAssessmentSubmissionID}
								messageID={messageID}
								postID={postID}
								studentNoteID={studentNoteID}
							/>
						))}
					</Row>
				</If>

				<If condition={fileAttachments.length > 0}>
					<Row justify="spaced-start" overflow="wrap" withVerticalSpacing>
						{fileAttachments.map((mediaAttachment) => (
							<EditPreview
								key={mediaAttachment.id}
								mediaAttachment={mediaAttachment}
								announcementID={announcementID}
								calendarEventID={calendarEventID}
								courseAssessmentID={courseAssessmentID}
								courseAssessmentSubmissionID={courseAssessmentSubmissionID}
								messageID={messageID}
								postID={postID}
								studentNoteID={studentNoteID}
							/>
						))}
					</Row>
				</If>

				<If condition={linkAttachments.length > 0}>
					<Row justify="spaced-start" overflow="wrap" withVerticalSpacing>
						{linkAttachments.map((mediaAttachment) => (
							<EditPreview
								key={mediaAttachment.id}
								mediaAttachment={mediaAttachment}
								announcementID={announcementID}
								calendarEventID={calendarEventID}
								courseAssessmentID={courseAssessmentID}
								courseAssessmentSubmissionID={courseAssessmentSubmissionID}
								messageID={messageID}
								postID={postID}
								studentNoteID={studentNoteID}
							/>
						))}
					</Row>
				</If>
				<Button variant="link" onClick={openCreateModal}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<Icon.Plus size={16} /> Add Attachment
					</Row>
				</Button>
			</Column>

			<Modal.If condition={isCreateModalOpen}>
				<Modal onClose={closeCreateModal} style={{ width: "30rem", height: "35rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Add Attachment</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<CreateOnExisting
							announcementID={announcementID}
							calendarEventID={calendarEventID}
							courseAssessmentID={courseAssessmentID}
							courseAssessmentSubmissionID={courseAssessmentSubmissionID}
							messageID={messageID}
							postID={postID}
							studentMedicalEventID={studentMedicalEventID}
							studentNoteID={studentNoteID}
							displayOrder={Math.max(mediaAttachments.length, ...mediaAttachments.map((e) => e.displayOrder + 1))}
							onSuccess={closeCreateModal}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

type EditPreviewProps = {
	mediaAttachment: Omit<
		MediaAttachmentDetailQuery["mediaAttachment"],
		| "announcement"
		| "calendarEvent"
		| "courseAssessment"
		| "courseAssessmentSubmission"
		| "message"
		| "post"
		| "studentMedicalEvent"
		| "studentNote"
	>;
	announcementID?: Announcement["id"] | null;
	calendarEventID?: CalendarEvent["id"] | null;
	courseAssessmentID?: CourseAssessment["id"] | null;
	courseAssessmentSubmissionID?: CourseAssessmentSubmission["id"] | null;
	messageID?: Message["id"] | null;
	postID?: Post["id"] | null;
	studentMedicalEventID?: StudentMedicalEvent["id"] | null;
	studentNoteID?: StudentNote["id"] | null;
};

function EditPreview({
	mediaAttachment,
	announcementID = null,
	calendarEventID = null,
	courseAssessmentID = null,
	courseAssessmentSubmissionID = null,
	messageID = null,
	postID = null,
	studentMedicalEventID = null,
	studentNoteID = null,
}: EditPreviewProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Conditional>
				<If condition={mediaAttachment.isFile && MediaAttachmentUtilities.isVisual(mediaAttachment)}>
					<VisualAttachmentPreview
						formValues={mediaAttachment}
						onClick={openModal}
						className={styles["media-attachment-preview"]}
					/>
				</If>
				<If condition={mediaAttachment.isFile}>
					<FileAttachmentPreview
						formValues={mediaAttachment}
						onClick={openModal}
						className={styles["media-attachment-preview"]}
					/>
				</If>
				<Else>
					<LinkAttachmentPreview
						formValues={mediaAttachment}
						onClick={openModal}
						className={styles["media-attachment-preview"]}
					/>
				</Else>
			</Conditional>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} style={{ width: "30rem", height: "35rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Edit Attachment</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<Detail
							mediaAttachment={mediaAttachment}
							announcementID={announcementID}
							calendarEventID={calendarEventID}
							courseAssessmentID={courseAssessmentID}
							courseAssessmentSubmissionID={courseAssessmentSubmissionID}
							messageID={messageID}
							postID={postID}
							studentMedicalEventID={studentMedicalEventID}
							studentNoteID={studentNoteID}
							onSuccess={closeModal}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

const detailFields = {
	isFile: true,
	fileKey: true,
	fileContentType: true,
	linkURL: true,
	title: true,
	description: true,
	taggedPersonIDs: true,
} as const;

type DetailProps = {
	mediaAttachment: Omit<
		MediaAttachmentDetailQuery["mediaAttachment"],
		| "announcement"
		| "calendarEvent"
		| "courseAssessment"
		| "courseAssessmentSubmission"
		| "message"
		| "post"
		| "studentMedicalEvent"
		| "studentNote"
	>;
	announcementID?: Announcement["id"] | null;
	calendarEventID?: CalendarEvent["id"] | null;
	courseAssessmentID?: CourseAssessment["id"] | null;
	courseAssessmentSubmissionID?: CourseAssessmentSubmission["id"] | null;
	messageID?: Message["id"] | null;
	postID?: Post["id"] | null;
	studentMedicalEventID?: StudentMedicalEvent["id"] | null;
	studentNoteID?: StudentNote["id"] | null;
	onSuccess: () => void;
};

function Detail({
	mediaAttachment,
	announcementID,
	calendarEventID,
	courseAssessmentID,
	courseAssessmentSubmissionID,
	messageID,
	postID,
	studentMedicalEventID,
	studentNoteID,
	onSuccess,
}: DetailProps) {
	const { formState, onSubmit } = useDetailForm({
		mediaAttachment,
		fields: detailFields,
		editMode: EditMode.WriteOnly,
		onSuccess,
	});

	useInternalField(formState, "isFile");

	const includeDescription = parentSupportsDescription({
		announcementID,
		calendarEventID,
		courseAssessmentID,
		courseAssessmentSubmissionID,
		messageID,
		postID,
		studentMedicalEventID,
		studentNoteID,
	});
	const includeTaggedPersonIDs = parentSupportsTaggedPersonIDs({
		announcementID,
		calendarEventID,
		courseAssessmentID,
		courseAssessmentSubmissionID,
		messageID,
		postID,
		studentMedicalEventID,
		studentNoteID,
	});

	const formName = `mediaAttachment.detail.${mediaAttachment.id}`;

	return (
		<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} name={formName} noChangesDialog>
			<Form.Column style={{ width: "100%" }}>
				<FieldConditional>
					<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
						<Field.File formState={formState} noHeader style={{ width: "100%" }} />
					</FieldIf>
					<FieldElse formState={formState} name="linkURL">
						<Field.LinkURL formState={formState} style={{ width: "100%" }} />
					</FieldElse>
				</FieldConditional>

				<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
					<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
				</FieldIf>

				<FieldIf
					formState={formState}
					names={inPostVisualFileConditionalFields}
					condition={
						formState.formValues.isFile &&
						MediaAttachmentUtilities.isVisual({ fileContentType: formState.formValues.fileContentType })
					}
				>
					<FieldIf formState={formState} name="description" condition={includeDescription}>
						<Field.Description
							formState={formState}
							noHeader
							placeholder="Write a caption..."
							style={{ width: "100%" }}
						/>
					</FieldIf>
					<FieldIf formState={formState} name="taggedPersonIDs" condition={includeTaggedPersonIDs}>
						<TaggedPeopleTooltipButton formState={formState} />
					</FieldIf>
				</FieldIf>

				<Form.ActionBar>
					<SubmitButton
						onClick={formState.onSubmitWrapper(onSubmit)}
						submissionStatus={formState.submissionStatus}
						disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
					>
						Save Changes
					</SubmitButton>
				</Form.ActionBar>

				<MediaAttachmentDeleteButton instanceID={mediaAttachment.id} size="small">
					Remove Attachment
				</MediaAttachmentDeleteButton>
			</Form.Column>
		</Form>
	);
}

type CreateOnExistingProps = {
	announcementID?: Announcement["id"] | null;
	calendarEventID?: CalendarEvent["id"] | null;
	courseAssessmentID?: CourseAssessment["id"] | null;
	courseAssessmentSubmissionID?: CourseAssessmentSubmission["id"] | null;
	messageID?: Message["id"] | null;
	postID?: Post["id"] | null;
	studentMedicalEventID?: StudentMedicalEvent["id"] | null;
	studentNoteID?: StudentNote["id"] | null;
	displayOrder?: number;
	onSuccess: () => void;
};

function CreateOnExisting({
	announcementID = null,
	calendarEventID = null,
	courseAssessmentID = null,
	courseAssessmentSubmissionID = null,
	messageID = null,
	postID = null,
	studentMedicalEventID = null,
	studentNoteID = null,
	displayOrder = 0,
	onSuccess,
}: CreateOnExistingProps) {
	const initialFormValues = React.useMemo<Partial<MediaAttachmentFormValues.Create>>(
		() => ({
			announcementID,
			calendarEventID,
			courseAssessmentID,
			courseAssessmentSubmissionID,
			messageID,
			postID,
			studentMedicalEventID,
			studentNoteID,
			displayOrder,
			isFile: true,
		}),
		[
			announcementID,
			calendarEventID,
			courseAssessmentID,
			courseAssessmentSubmissionID,
			messageID,
			postID,
			studentMedicalEventID,
			studentNoteID,
			displayOrder,
		],
	);

	const { formState, onSubmit } = useCreateForm({ initialFormValues, onSuccess });

	useInternalField(formState, "announcementID");
	useInternalField(formState, "calendarEventID");
	useInternalField(formState, "courseAssessmentID");
	useInternalField(formState, "courseAssessmentSubmissionID");
	useInternalField(formState, "messageID");
	useInternalField(formState, "postID");
	useInternalField(formState, "studentMedicalEventID");
	useInternalField(formState, "studentNoteID");
	useInternalField(formState, "isFile");
	useInternalField(formState, "displayOrder");

	const includeDescription = parentSupportsDescription({
		announcementID,
		calendarEventID,
		courseAssessmentID,
		courseAssessmentSubmissionID,
		messageID,
		postID,
		studentMedicalEventID,
		studentNoteID,
	});
	const includeTaggedPersonIDs = parentSupportsTaggedPersonIDs({
		announcementID,
		calendarEventID,
		courseAssessmentID,
		courseAssessmentSubmissionID,
		messageID,
		postID,
		studentMedicalEventID,
		studentNoteID,
	});

	const formName = `mediaAttachment.create.${
		announcementID ??
		calendarEventID ??
		courseAssessmentID ??
		courseAssessmentSubmissionID ??
		messageID ??
		postID ??
		studentMedicalEventID ??
		studentNoteID
	}`;

	return (
		<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} name={formName}>
			<Form.Column style={{ width: "100%" }}>
				<FieldConditional>
					<FieldIf formState={formState} names={fileFieldNames} condition={formState.formValues.isFile}>
						<Field.File formState={formState} noHeader style={{ width: "100%" }} />
					</FieldIf>
					<FieldElse formState={formState} name="linkURL">
						<Field.LinkURL formState={formState} style={{ width: "100%" }} />
					</FieldElse>
				</FieldConditional>

				<FieldIf formState={formState} name="title" condition={!!formState.formValues.fileKey}>
					<Field.Title formState={formState} placeholder="Add a title..." noHeader style={{ width: "100%" }} />
				</FieldIf>

				<FieldIf
					formState={formState}
					names={inPostVisualFileConditionalFields}
					condition={
						formState.formValues.isFile &&
						MediaAttachmentUtilities.isVisual({ fileContentType: formState.formValues.fileContentType })
					}
				>
					<FieldIf formState={formState} name="description" condition={includeDescription}>
						<Field.Description
							formState={formState}
							noHeader
							placeholder="Write a caption..."
							style={{ width: "100%" }}
						/>
					</FieldIf>
					<FieldIf formState={formState} name="taggedPersonIDs" condition={includeTaggedPersonIDs}>
						<TaggedPeopleTooltipButton formState={formState} />
					</FieldIf>
				</FieldIf>

				<Form.ActionBar>
					<SubmitButton
						onClick={formState.onSubmitWrapper(onSubmit)}
						submissionStatus={formState.submissionStatus}
						disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
					>
						Add Attachment
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type FieldSupportArgs = {
	announcementID?: Announcement["id"] | null;
	calendarEventID?: CalendarEvent["id"] | null;
	courseAssessmentID?: CourseAssessment["id"] | null;
	courseAssessmentSubmissionID?: CourseAssessmentSubmission["id"] | null;
	messageID?: Message["id"] | null;
	postID?: Post["id"] | null;
	studentMedicalEventID?: StudentMedicalEvent["id"] | null;
	studentNoteID?: StudentNote["id"] | null;
};

function parentSupportsDescription({ courseAssessmentID, postID }: FieldSupportArgs) {
	return !!courseAssessmentID || !!postID;
}

function parentSupportsTaggedPersonIDs({ postID }: FieldSupportArgs) {
	return !!postID;
}
